import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Globe, Home as HomeIcon, Users, Building2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";
import { useSegment } from "@/context/CustomerSegmentContext";
import { LOGO_URL } from "@/data/projectImages";
import { serviceCatalog } from "@/data/services";

const Header = () => {
  const { t, lang, setLang, languages } = useT();
  const { segment, setSegment } = useSegment();
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);
  const langRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setServicesOpen(false);
      if (aboutRef.current && !aboutRef.current.contains(e.target)) setAboutOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const currentLang = languages.find((l) => l.code === lang);

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all duration-300 ${
        scrolled ? "bg-white/95 border-b border-zinc-200 shadow-sm" : "bg-white/85 border-b border-zinc-100"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20 gap-2 sm:gap-4">
        {/* Logo only - no text */}
        <Link to="/" className="flex items-center flex-shrink-0 group" data-testid="logo-link">
          <motion.img
            src={LOGO_URL}
            alt="SF-Maler Malergeschäft Sammy Felber Basel"
            className="h-10 sm:h-14 w-auto"
            whileHover={{ rotate: -3, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center" data-testid="desktop-nav">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "text-red-600" : "text-zinc-700 hover:text-zinc-900"}`
            }
            data-testid="nav-link-home"
          >
            {t.nav.home}
          </NavLink>

          {/* SERVICES DROPDOWN */}
          <div ref={servicesRef} className="relative">
            <button
              onClick={() => { setServicesOpen(!servicesOpen); setAboutOpen(false); }}
              onMouseEnter={() => { setServicesOpen(true); setAboutOpen(false); }}
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-zinc-700 hover:text-zinc-900 transition-colors"
              data-testid="nav-services-dropdown"
            >
              {t.nav.services}
              <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  onMouseLeave={() => setServicesOpen(false)}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[460px]"
                >
                  <div className="bg-white border border-zinc-200 shadow-2xl">
                    <div className="grid grid-cols-2 gap-px bg-zinc-100">
                      {serviceCatalog.map((s) => {
                        const Icon = s.icon;
                        const item = t.services.items[s.key];
                        return (
                          <Link
                            key={s.slug}
                            to={`/dienstleistungen/${s.slug}`}
                            onClick={() => setServicesOpen(false)}
                            className="bg-white p-3.5 flex items-start gap-3 hover:bg-red-50 transition-colors group"
                            data-testid={`dd-service-${s.slug}`}
                          >
                            <Icon size={18} className="text-red-600 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                            <div className="font-display font-bold text-sm text-zinc-900 group-hover:text-red-600 transition-colors leading-tight">
                              {item.title}
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    <Link
                      to="/dienstleistungen"
                      onClick={() => setServicesOpen(false)}
                      className="block bg-zinc-900 text-white text-center py-3 text-sm font-bold hover:bg-red-600 transition-colors"
                      data-testid="dd-all-services"
                    >
                      {t.nav.all_services} →
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink
            to="/referenzen"
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "text-red-600" : "text-zinc-700 hover:text-zinc-900"}`
            }
            data-testid="nav-link-referenzen"
          >
            {t.nav.gallery}
          </NavLink>

          {/* ABOUT DROPDOWN */}
          <div ref={aboutRef} className="relative">
            <button
              onClick={() => { setAboutOpen(!aboutOpen); setServicesOpen(false); }}
              onMouseEnter={() => { setAboutOpen(true); setServicesOpen(false); }}
              className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-zinc-700 hover:text-zinc-900 transition-colors"
              data-testid="nav-about-dropdown"
            >
              {t.nav.about}
              <ChevronDown size={14} className={`transition-transform ${aboutOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {aboutOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.98 }}
                  transition={{ duration: 0.15 }}
                  onMouseLeave={() => setAboutOpen(false)}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-2 min-w-[220px]"
                >
                  <div className="bg-white border border-zinc-200 shadow-2xl overflow-hidden">
                    <Link
                      to="/ueber-uns"
                      onClick={() => setAboutOpen(false)}
                      className="flex items-center gap-3 px-5 py-4 hover:bg-red-50 transition-colors group border-b border-zinc-100"
                      data-testid="dd-about-overview"
                    >
                      <HomeIcon size={18} className="text-red-600" strokeWidth={1.5} />
                      <div>
                        <div className="font-display font-bold text-sm text-zinc-900 group-hover:text-red-600">{t.nav.about}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">{t.nav.about_overview}</div>
                      </div>
                    </Link>
                    <Link
                      to="/team"
                      onClick={() => setAboutOpen(false)}
                      className="flex items-center gap-3 px-5 py-4 hover:bg-red-50 transition-colors group"
                      data-testid="dd-about-team"
                    >
                      <Users size={18} className="text-red-600" strokeWidth={1.5} />
                      <div>
                        <div className="font-display font-bold text-sm text-zinc-900 group-hover:text-red-600">{t.nav.team}</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Sammy Felber & Team</div>
                      </div>
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink
            to="/kontakt"
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "text-red-600" : "text-zinc-700 hover:text-zinc-900"}`
            }
            data-testid="nav-link-kontakt"
          >
            {t.nav.contact}
          </NavLink>
        </nav>

        {/* Right side controls */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* SEGMENTED CONTROL – Privatkunden / Gewerbe */}
          <div
            className="hidden xl:flex items-center bg-zinc-100 p-1 rounded-full relative"
            role="tablist"
            aria-label="Kundensegment wechseln"
            data-testid="segment-control"
          >
            {[
              { value: "private", label: t.segment.private, icon: HomeIcon },
              { value: "commercial", label: t.segment.commercial, icon: Building2 },
            ].map((seg) => {
              const active = segment === seg.value;
              const Icon = seg.icon;
              return (
                <button
                  key={seg.value}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setSegment(seg.value)}
                  className={`relative flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-full transition-colors ${
                    active ? "text-white" : "text-zinc-600 hover:text-zinc-900"
                  }`}
                  data-testid={`segment-${seg.value}`}
                >
                  {active && (
                    <motion.div
                      layoutId="segmentPill"
                      className="absolute inset-0 bg-red-600 rounded-full"
                      transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                    />
                  )}
                  <Icon size={13} className="relative z-10" strokeWidth={2.2} />
                  <span className="relative z-10 whitespace-nowrap">{seg.label}</span>
                </button>
              );
            })}
          </div>

          {/* Compact Language switcher */}
          <div className="relative hidden sm:block" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 px-3 py-2 border border-zinc-200 hover:border-zinc-400 transition-colors text-sm font-bold text-zinc-800"
              data-testid="lang-switcher"
            >
              <Globe size={14} />
              {currentLang?.label}
              <ChevronDown size={12} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-1 bg-white border border-zinc-200 shadow-xl min-w-[80px] z-50"
                >
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`w-full px-4 py-2 text-sm font-bold text-left transition-colors ${
                        lang === l.code ? "bg-red-600 text-white" : "text-zinc-700 hover:bg-zinc-50"
                      }`}
                      data-testid={`lang-${l.code}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Angular red Call CTA (kept as original) - only visible on desktop lg+ */}
          <a href="tel:+41796070148" className="!hidden lg:!inline-flex btn-primary btn-glow" data-testid="header-cta-call">
            <Phone size={16} />
            {t.common.cta_call}
          </a>

          {/* Mobile menu trigger */}
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-1.5 sm:p-2 text-zinc-900"
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Sub-header segment bar (visible on medium screens where xl segment is hidden) */}
      <div className="hidden md:flex xl:hidden border-t border-zinc-100 bg-zinc-50/60">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full flex items-center justify-center py-2 gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            {lang === "de" ? "Ich bin" : lang === "fr" ? "Je suis" : lang === "it" ? "Sono" : "I am"}:
          </span>
          <div className="flex items-center bg-zinc-100 p-1 rounded-full">
            {[
              { value: "private", label: t.segment.private_short, icon: HomeIcon },
              { value: "commercial", label: t.segment.commercial_short, icon: Building2 },
            ].map((seg) => {
              const active = segment === seg.value;
              const Icon = seg.icon;
              return (
                <button
                  key={seg.value}
                  onClick={() => setSegment(seg.value)}
                  className={`relative flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full transition-colors ${
                    active ? "bg-red-600 text-white" : "text-zinc-600"
                  }`}
                  data-testid={`segment-mid-${seg.value}`}
                >
                  <Icon size={12} strokeWidth={2.2} />
                  {seg.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-zinc-200 bg-white overflow-hidden"
            data-testid="mobile-nav"
          >
            <nav className="px-4 py-4 flex flex-col gap-1">
              {/* Mobile segment control */}
              <div className="flex items-center bg-zinc-100 p-1 rounded-full mb-3">
                {[
                  { value: "private", label: t.segment.private, icon: HomeIcon },
                  { value: "commercial", label: t.segment.commercial, icon: Building2 },
                ].map((seg) => {
                  const active = segment === seg.value;
                  const Icon = seg.icon;
                  return (
                    <button
                      key={seg.value}
                      onClick={() => setSegment(seg.value)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold rounded-full transition-colors ${
                        active ? "bg-red-600 text-white" : "text-zinc-600"
                      }`}
                      data-testid={`msegment-${seg.value}`}
                    >
                      <Icon size={14} strokeWidth={2.2} />
                      {seg.label}
                    </button>
                  );
                })}
              </div>

              {[
                { to: "/", label: t.nav.home },
                { to: "/dienstleistungen", label: t.nav.services },
                { to: "/referenzen", label: t.nav.gallery },
                { to: "/ueber-uns", label: t.nav.about },
                { to: "/team", label: t.nav.team },
                { to: "/kontakt", label: t.nav.contact },
              ].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base font-semibold border-l-2 ${
                      isActive ? "border-red-600 text-red-600 bg-red-50" : "border-transparent text-zinc-700"
                    }`
                  }
                  data-testid={`mnav-${l.to.replace(/\//g, "") || "home"}`}
                >
                  {l.label}
                </NavLink>
              ))}

              <div className="flex items-center gap-1 mt-3 border border-zinc-200 p-1 w-fit">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 text-xs font-bold ${
                      lang === l.code ? "bg-zinc-900 text-white" : "text-zinc-600"
                    }`}
                    data-testid={`mlang-${l.code}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <a href="tel:+41796070148" className="mt-3 btn-primary btn-glow w-full justify-center" data-testid="mobile-cta-call">
                <Phone size={16} /> {t.common.cta_call}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
